import React, { useState } from 'react'
import cx from 'classnames'
import styles from './ArtworkDetail.module.scss'
import { Col, Container, Row } from 'react-grid-system'
import { Breadcrumb, Button, Heading, Paragraph, ShareButton } from '~elements'
import { useWindowSize } from 'utils/hooks'
import { Video, StudentArtModal } from '@msgtechnology/sphere'
import { submitXOVote } from 'lib/voting'
import { XO_PATHNAME_TO_DISPLAYNAME } from '~constants'
import { useRouter } from 'next/router'

interface ArtworkDetailProps {
  description: {
    html: string
  }
  headline: string
  eyebrow: string
  videoDetails: {
    url: string
    posterImage: string
  }
  educationLevel: number
  sId: string
  schoolName: string
  artWorkTitle: string
  createdAt: string
  votingModalDisclaimer?: string
  votingModalReminder?: string
  isVotingOpen: () => boolean
  groupVotingButton: JSX.Element | undefined
}

const ArtworkDetail = ({
  description,
  headline,
  videoDetails,
  educationLevel,
  sId,
  schoolName,
  artWorkTitle,
  createdAt,
  votingModalReminder,
  votingModalDisclaimer,
  isVotingOpen,
  groupVotingButton
}: ArtworkDetailProps) => {
  const { isBreakpoint: isBelowBreakpointSm } = useWindowSize('sm', '<')
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const router = useRouter()
  const { query } = router

  const handleModalClose = () => setModalIsOpen(false)
  const handleModalOpen: () => void = () => {
    if (isVotingOpen()) {
      setModalIsOpen(true)
    }
  }
  const confirmationModalCloseAction = () => {
    if (query.slug && typeof query.slug === 'object') {
      const gallerySlug = query.slug.join('/')
      router.push(`/${gallerySlug}`)
    }
  }

  const ButtonComponent = () => {
    return (
      <Button
        color='light'
        fill='solid'
        isFullWidth={false}
        size='md'
        title='Vote'
        onClick={handleModalOpen}
        justify='right'
        analytics={{ linkModule: 'artwork_details' }}
      />
    )
  }
  return (
    <Container className={styles['artwork-container']}>
      <Breadcrumb pathNameToDisplayName={XO_PATHNAME_TO_DISPLAYNAME} />
      <Row>
        <Col>
          <div className={cx([styles['artwork-details-container']])}>
            <div className={styles['artwork-details']}>
              <div className={styles['headline-container']}>
                <div className={styles['headlinegap']}>
                  <Heading level={1} justify={'left'} noMarginBottom>
                    <span className={styles['headline']}>{headline}</span>
                  </Heading>
                  {!isBelowBreakpointSm && (
                    <div className={styles['btn-container']}>
                      <ShareButton isFullWidth={false} title={headline} size='md' />
                      {isVotingOpen() && ButtonComponent()}
                    </div>
                  )}
                </div>
                <div className={styles['description']}>
                  <div
                    className={isBelowBreakpointMd ? styles['extended-title-mobile'] : styles['extended-title-desktop']}
                  >
                    <Paragraph
                      fontSize={isBelowBreakpointSm ? 'sm' : isBelowBreakpointMd ? 'md' : 'lg'}
                      text={description}
                      justify={'left'}
                    />
                  </div>
                  {isBelowBreakpointMd && (
                    <div className={styles['btn-container-mobile']}>
                      <ShareButton isFullWidth={true} title={headline} />
                      {isVotingOpen() && ButtonComponent()}
                    </div>
                  )}
                </div>

                <Video
                  id={sId}
                  video={{
                    url: videoDetails.url,
                    posterImage: videoDetails.posterImage
                  }}
                  displayThumbnail={false}
                  autoplay={true}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <StudentArtModal
        handleModalClose={handleModalClose}
        modalIsOpen={modalIsOpen}
        submitVote={submitXOVote}
        videoVoteInfo={{
          id: sId,
          educationLevel: educationLevel
        }}
        // This share button will appear in the confirmation modal after a user successfully submits their vote.
        shareButton={<ShareButton isFullWidth={true} title={headline} size='md' type={'result-modal'} />}
        reminder={votingModalReminder}
        disclaimer={votingModalDisclaimer}
        isVotingOpen={isVotingOpen}
        confirmationModalCloseAction={confirmationModalCloseAction}
        // @ts-ignore - TODO: address in @msgtechnology/sphere package
        votingButtons={groupVotingButton}
      >
        <Video
          id={sId}
          video={{
            url: videoDetails.url,
            posterImage: videoDetails.posterImage
          }}
          displayThumbnail={false}
          autoplay={true}
          voteDetails={{
            studentName: headline,
            educationLevel: educationLevel,
            schoolName: schoolName,
            artWorkTitle: artWorkTitle,
            createdAt: createdAt,
            submitVote: submitXOVote,
            isVotingOpen: isVotingOpen
          }}
        />
      </StudentArtModal>
    </Container>
  )
}

export default ArtworkDetail
